import { Injectable } from '@angular/core';
import { LoggerService } from '../../services/logger.service';
import { TemplateService } from '../../car-wash-website/services/template.service';
import { TemplatePopulatorService } from './template-populator.service';
import { WebsiteElement } from '@way/website-themes/themes/minimal_website/home-page-preview/home-page-preview.component';
import { HeroPanelData } from '../components/template/panels/hero-panel';
import { FooterData } from '../components/footer-panel/footer-panel.component';
import { HeaderData } from '../components/header-panel/header-panel.component';
import { BrandingData } from '../components/branding-panel/branding-panel.component';
import { ToggleCardData } from '../../shared/components/toggle-card/toggle-card.component';

/**
 * Mutable context bag for data-loading and page/section management.
 * Passed by the component; mutated in-place.
 */
export interface DataContext {
  // Identity
  websiteId: string | null;
  websitePageId: string | null;
  templateId: string | null;
  templatePageId: string | null;
  currentTemplateId: string | null;
  // Loading state
  loading: boolean;
  error: string | null;
  // Loaded data
  website: any;
  websiteData: any;
  pages: ToggleCardData[];
  sections: any[];
  templateData: any;
  templates: any[];
  currentPageType: string;
  // JSON / elements
  json: any;
  elements: WebsiteElement[];
  // Panel data (populated during load)
  heroPanelData: HeroPanelData;
  footerData: FooterData;
  headerData: HeaderData;
  brandingData: BrandingData;
  primaryColor: string | null;
  headingColor: string | null;
  paragraphColor: string | null;
  headingFont: string | null;
  bodyFont: string | null;
  // Panel visibility
  showHeroPanel: boolean;
  showBrandingPanel: boolean;
  // Cache fields
  _pagesForNavigation: any[];
  _pagesForNavigationCacheKey: string;
  _sectionVisibilityMap: Record<string, boolean>;
  _sectionVisibilityMapCacheKey: string;
  // Callbacks
  closeAllPanels(): void;
  updateTemplateSelection(id: string): void;
  initializeMasterState(description?: string): void;
  recordStateChange(description?: string, immediate?: boolean): void;
  scheduleAutosave(paths: string[]): void;
  generateHtmlFromJson(json: any): string;
  cleanupJsonStructure(json: any): any;
  populateHeroFromTemplate(): void;
  populateSectionsFromTemplate(): void;
  populateTestimonialsFromTemplate(): void;
  populateBookingFromTemplate(): void;
  populateContactFromTemplate(): void;
  populateHeaderFromTemplate(): void;
  populateFooterFromTemplate(): void;
  populateFromJson(json: any): void;
  buildElementsFromJsonSections(): void;
  ensureBaseElements(): void;
}

/**
 * Handles all data-loading (template, website, pages) and page/section
 * management logic extracted from the website-builder component.
 */
@Injectable({ providedIn: 'root' })
export class BuilderDataService {

  constructor(
    private logger: LoggerService,
    private templateService: TemplateService,
    private templatePopulator: TemplatePopulatorService
  ) {}
  /**
   * Loads template data for the active builder context and initializes the page, sections, and UI state.
   * @example
   * oadTemplateData(ctx)
   * void
   * @param {DataContext} ctx - Builder data context used to store template, page, loading, and error state.
   * @returns {void} Nothing is returned.
   */
  loadTemplateData(ctx: DataContext, onContextChange?: () => void): void {
    if (!ctx.templateId) return;

    ctx.loading = true;
    ctx.error = null;

    this.templateService.getTemplateById(ctx.templateId).subscribe({
      next: (response) => {
        if (response.status && response.data) {
          // Handle both array and single object responses
          const templates = Array.isArray(response.data) ? response.data : [response.data];

          if (templates.length === 0) {
            ctx.error = 'Template not found';
            ctx.loading = false;
            onContextChange?.();
            return;
          }

          // Load all template pages into the pages array for the sidebar
          this.loadPagesFromTemplate(templates, ctx);

          // Find the Home page in the loaded pages array
          const homePageIndex = ctx.pages.findIndex((p: any) => p.isHomePage);
          const homePage = homePageIndex >= 0 ? ctx.pages[homePageIndex] : (ctx.pages.length > 0 ? ctx.pages[0] : null);

          if (homePage) {
            // Automatically load the Home page data (same as clicking on it)
            this.loadPageData(homePage as any, homePageIndex, ctx);
          } else {
            // Fallback: use first template/page as default
            const homeTemplate = templates.find((t: any) => {
              const title = t?.json_data?.page?.title || t?.name || '';
              return title.toLowerCase().trim() === 'home';
            });
            const templateData = homeTemplate || templates[0];
            ctx.templateData = templateData;
            this.logger.debug('Template data loaded (Home page):', ctx.templateData);

            // Cache ids and JSON for manual editing/saving
            ctx.templatePageId = (ctx.templateData as any)?.template_page_id || (ctx.templateData as any)?.id || null;
            ctx.json = ctx.templateData.json_data || {};

            // Build elements from JSON sections in correct order (if JSON data available)
            // Otherwise fall back to ensureBaseElements
            if (ctx.json?.page?.sections) {
              ctx.buildElementsFromJsonSections();
            } else {
              ctx.ensureBaseElements();
            }
            
            // Ensure no elements are selected and all panels are closed
            ctx.elements.forEach(e => e.selected = false);
            ctx.closeAllPanels();

            // Populate header section data from template
            ctx.populateHeaderFromTemplate();

            // Populate hero section data from template
            ctx.populateHeroFromTemplate();

            // Populate sections data from template
            ctx.populateSectionsFromTemplate();

            // Populate testimonials data from template
            ctx.populateTestimonialsFromTemplate();

            // Populate booking data from template
            ctx.populateBookingFromTemplate();

            // Populate contact data from template
            ctx.populateContactFromTemplate();

            // Populate footer data from template
            ctx.populateFooterFromTemplate();

            // Load sections from template
            this.loadSectionsFromPage(ctx);
          }
        } else {
          ctx.error = response.message || 'Failed to load template';
        }
        ctx.loading = false;
        onContextChange?.();
      },
      error: (err) => {
        ctx.error = 'Failed to load template. Please try again.';
        ctx.loading = false;
        onContextChange?.();
        this.logger.error('Error loading template:', err);
      }
    });
  }

  /**
   * Loads a website by its ID, initializes the builder state, and populates pages, branding, and page content from the API response.
   * @example
   * oadWebsiteById('123', ctx)
   * void
   * @param {string} id - The website ID to fetch from the API.
   * @param {DataContext} ctx - The builder/data context used to store loading state, website data, pages, branding, and selected content.
   * @returns {void} No return value.
   */
  loadWebsiteById(id: string, ctx: DataContext, onContextChange?: () => void): void {
    ctx.loading = true;
    ctx.error = null;
    this.templateService.getWebsiteById(id).subscribe({
      next: (resp) => {
        const data = resp?.data || {};

        // Handle different response structures:
        // 1. New structure: data is the website object directly
        // 2. Old structure: data.website contains the website
        const websiteBase = data.website || data;
        const websitePages = Array.isArray(data.website_pages)
          ? data.website_pages
          : (Array.isArray(data.pages) ? data.pages : []);

        // Store the website object for later use (e.g., footer data).
        // Always merge publish flags from the API root — they may not exist on nested website.
        ctx.website = {
          ...websiteBase,
          id: websiteBase.id || data.id,
          publish: data.publish ?? websiteBase.publish ?? false,
          unpublish: data.unpublish ?? websiteBase.unpublish ?? false
        };
        const website = ctx.website;

        this.logger.debug('Loading website by ID:', id);
        this.logger.debug('Website data:', website);
        this.logger.debug('Website pages:', websitePages);

        // Ensure all panels are closed and no elements are selected
        ctx.closeAllPanels();
        if (ctx.elements.length > 0) {
          ctx.elements.forEach(e => e.selected = false);
        }

        // Load all pages into the pages array for the sidebar
        this.loadPagesFromWebsite(websitePages, ctx);

        // Find the Home page in the loaded pages array
        const homePageIndex = ctx.pages.findIndex((p: any) => p.isHomePage);
        const homePage = homePageIndex >= 0 ? ctx.pages[homePageIndex] : (ctx.pages.length > 0 ? ctx.pages[0] : null);

        ctx.websiteId = website.id || ctx.websiteId;
        ctx.templateId = website.template_id || ctx.templateId;
        ctx.currentTemplateId = website.template_id || ctx.templateId; // Store for template switching
        try { if (ctx.templateId) sessionStorage.setItem('templateId', ctx.templateId); } catch { /* ignore */ }

        // Mark matching template as selected if templates are already loaded
        if (ctx.templateId && ctx.templates.length > 0) {
          ctx.updateTemplateSelection(ctx.templateId);
        }

        // Load branding data (logo URL, colors, fonts) from website
        // Check multiple possible locations for branding data
        let logoUrl = null;
        let colors = null;
        let fonts = null;

        // First check website->json_data->website->branding (new nested structure)
        if (website.json_data?.website?.branding) {
          const branding = website.json_data.website.branding;
          logoUrl = branding.logo?.url || null;
          colors = branding.colors || null;
          fonts = branding.fonts || null;
        }
        // Check website->json_data->branding (alternative structure)
        else if (website.json_data?.branding) {
          const branding = website.json_data.branding;
          logoUrl = branding.logo?.url || null;
          colors = branding.colors || null;
          fonts = branding.fonts || null;
        }
        // Fallback to website.brand_logo (old structure)
        else if (website.brand_logo) {
          logoUrl = website.brand_logo;
        }

        // Update branding data with loaded values
        ctx.brandingData = {
          logoUrl: logoUrl || ctx.brandingData.logoUrl,
          colors: colors || {
            primary: '#2C2C2C',
            heading: '#E9E9E9',
            paragraph: '#2C2C2C'
          },
          fonts: fonts || {
            heading: 'Sarabun',
            body: 'Open Sans'
          }
        };
        
        // Set primary color from website-level branding (highest priority)
        if (colors?.primary) {
          ctx.primaryColor = colors.primary;
        }
        
        // Set heading color from website-level branding
        if (colors?.heading) {
          ctx.headingColor = colors.heading;
        }
        
        // Set paragraph color from website-level branding
        if (colors?.paragraph) {
          ctx.paragraphColor = colors.paragraph;
        }
        
        // Set fonts from website-level branding
        if (fonts?.heading) {
          ctx.headingFont = fonts.heading;
        }
        
        if (fonts?.body) {
          ctx.bodyFont = fonts.body;
        }

        this.logger.debug('Branding data loaded:', ctx.brandingData);

        // If no pages found, try to load from template or create default
        if (ctx.pages.length === 0) {
          this.logger.warn('No pages found in website response. Attempting to load from template...');

          // If we have a template_id, try to load template pages
          if (ctx.templateId) {
            this.templateService.getTemplateById(ctx.templateId).subscribe({
              next: (templateResp) => {
                if (templateResp.status && templateResp.data) {
                  const templates = Array.isArray(templateResp.data) ? templateResp.data : [templateResp.data];
                  this.loadPagesFromTemplate(templates, ctx);

                  // After loading pages from template, load the first page
                  if (ctx.pages.length > 0) {
                    const firstPage = ctx.pages[0];
                    this.loadPageData(firstPage as any, 0, ctx);
                    ctx.loading = false;
                    onContextChange?.();
                  } else {
                    // Still no pages, try to use website json_data to create a default page
                    ctx.ensureBaseElements();
                    ctx.currentPageType = 'home';
                    if (website.json_data?.website) {
                      // Transform website-level json_data to page structure
                      const websiteData = website.json_data.website;
                      ctx.json = {
                        page: {
                          title: 'Home',
                          sections: {
                            header: websiteData.header || {},
                            hero_section: {},
                            info_section: {},
                            testimonials_section: {},
                            booking_section: {},
                            contact_section: {}
                          },
                          branding: websiteData.branding || {}
                        }
                      };
                      ctx.templateData = {
                        id: website.id,
                        name: website.name || 'Home',
                        json_data: ctx.json
                      } as any;

                      try { /* intentionally empty */ } catch { /* ignore */ }

                      ctx.populateHeaderFromTemplate();
                      ctx.populateHeroFromTemplate();
                      ctx.populateSectionsFromTemplate();
                      ctx.populateTestimonialsFromTemplate();
                      ctx.populateBookingFromTemplate();
                      ctx.populateContactFromTemplate();
                      ctx.populateFooterFromTemplate();
                      this.loadSectionsFromPage(ctx);
                      
                      // Initialize master state for undo/redo
                      setTimeout(() => ctx.initializeMasterState('Website loaded from API'), 100);
                    }
                    ctx.loading = false;
                    onContextChange?.();
                  }
                } else {
                  ctx.loading = false;
                  onContextChange?.();
                }
              },
              error: (err) => {
                this.logger.error('Failed to load template for pages:', err);
                // Set loading to false even if template load fails
                ctx.loading = false;
                onContextChange?.();
              }
            });
          } else {
            // No template ID, try to use website json_data to create a default page
            this.logger.warn('No template ID available. Attempting to use website json_data...');
            ctx.ensureBaseElements();
            ctx.currentPageType = 'home';
            if (website.json_data?.website) {
              // Transform website-level json_data to page structure
              const websiteData = website.json_data.website;
              ctx.json = {
                page: {
                  title: 'Home',
                  sections: {
                    header: websiteData.header || {},
                    hero_section: {},
                    info_section: {},
                    testimonials_section: {},
                    booking_section: {},
                    contact_section: {}
                  },
                  branding: websiteData.branding || {}
                }
              };
              ctx.templateData = {
                id: website.id,
                name: website.name || 'Home',
                json_data: ctx.json
              } as any;

              try { /* intentionally empty */ } catch { /* ignore */ }

              ctx.populateHeaderFromTemplate();
              ctx.populateHeroFromTemplate();
              ctx.populateSectionsFromTemplate();
              ctx.populateTestimonialsFromTemplate();
              ctx.populateBookingFromTemplate();
              ctx.populateContactFromTemplate();
              ctx.populateFooterFromTemplate();
              this.loadSectionsFromPage(ctx);
              
              // Initialize master state for undo/redo
              setTimeout(() => ctx.initializeMasterState('Website loaded from API'), 100);
            }
            ctx.loading = false;
            onContextChange?.();
          }
        } else if (homePage) {
          // Automatically load the Home page data (same as clicking on it)
          this.loadPageData(homePage as any, homePageIndex, ctx);
          ctx.loading = false;
          
          // Initialize master state for undo/redo
          setTimeout(() => ctx.initializeMasterState('Website loaded from API'), 100);
          onContextChange?.();
        } else {
          // Fallback: use first page
          const page = websitePages.length > 0 ? websitePages[0] : null;
          if (page) {
            // Set template data to the page for populate methods
            ctx.templateData = {
              id: page.template_page_id || page.id,
              name: page.name,
              json_data: page.json_data
            } as any;

            ctx.templatePageId = page?.template_page_id || ctx.templatePageId;
            ctx.websitePageId = page?.id || null;
            ctx.json = page?.json_data || {};

            // Set current page type based on page name
            const pageName = page.name?.toLowerCase().trim() || '';
            if (pageName === 'home' || pageName === 'homepage') {
              ctx.currentPageType = 'home';
            } else {
              ctx.currentPageType = (ctx.pages[0] as any)?.pageType || 'home';
            }

            try { /* intentionally empty */ } catch { /* ignore */ }

            // Update website data title if available
            if (ctx.json?.page?.title) {
              ctx.websiteData.title = ctx.json.page.title;
            } else if (page.name) {
              ctx.websiteData.title = page.name;
            }

            ctx.ensureBaseElements();
            ctx.populateFromJson(ctx.json);

            // Load sections from current page
            this.loadSectionsFromPage(ctx);
            
            // Initialize master state for undo/redo
            setTimeout(() => ctx.initializeMasterState('Website loaded from API'), 100);
          } else {
            // No pages at all - create a minimal default structure
            this.logger.warn('No pages available. Creating minimal default structure...');
            ctx.ensureBaseElements();
            ctx.currentPageType = 'home';
            ctx.json = {
              page: {
                title: 'Home',
                sections: {
                  header: {},
                  hero_section: {},
                  info_section: {},
                  testimonials_section: {},
                  booking_section: {},
                  contact_section: {}
                }
              }
            };
            ctx.templateData = {
              id: website.id,
              name: website.name || 'Home',
              json_data: ctx.json
            } as any;
            ctx.populateHeaderFromTemplate();
            ctx.populateHeroFromTemplate();
            this.loadSectionsFromPage(ctx);
            
            // Initialize master state for undo/redo
            setTimeout(() => ctx.initializeMasterState('Website loaded from API'), 100);
          }
          ctx.loading = false;
          onContextChange?.();
        }
      },
      error: (err) => {
        ctx.loading = false;
        ctx.error = 'Failed to load website. Please try again.';
        onContextChange?.();
        this.logger.error('Failed to load website by id:', err);
        this.logger.error('Error details:', JSON.stringify(err, null, 2));
      }
    });
  }

  /**
   * Load pages from website API response into the pages array for sidebar display
   */
  loadPagesFromWebsite(websitePages: any[], ctx: DataContext): void {
    if (!Array.isArray(websitePages) || websitePages.length === 0) {
      this.logger.warn('No website pages found in API response');
      ctx.pages = [];
      return;
    }

    ctx.pages = websitePages.map((page: any, index: number) => {
      // Prioritize the 'name' field from API, then fallback to json_data.page.title
      let pageName = 'Page ' + (index + 1);

      if (page?.name) {
        pageName = page.name;
      } else if (page?.json_data?.page?.title) {
        pageName = page.json_data.page.title;
      }

      // Set page type based on name (lowercase, no spaces)
      // Normalize page names to match expected page types
      const normalizedName = pageName.toLowerCase().trim();
      let pageType = normalizedName.replace(/\s+/g, '-').replace(/\|/g, '').replace(/[^a-z0-9-]/g, '');

      // Map common page name variations to expected page types
      const pageTypeMap: Record<string, string> = {
        'express-washes': 'express-wash-packages',
        'express-wash': 'express-wash-packages',
        'express-wash-packages': 'express-wash-packages',
        'fleets': 'fleet-management',
        'fleet': 'fleet-management',
        'fleet-management': 'fleet-management',
        'coupons': 'car-wash-coupons--way',
        'coupon': 'car-wash-coupons--way',
        'express-details': 'express-auto-details',
        'express-auto-details': 'express-auto-details',
        'home': 'home',
        'homepage': 'home'
      };

      // Use mapped page type if available, otherwise use generated one
      if (pageTypeMap[pageType]) {
        pageType = pageTypeMap[pageType];
      } else if (pageTypeMap[normalizedName]) {
        pageType = pageTypeMap[normalizedName];
      }

      // Check if this is the Home page (will be editable)
      const isHomePage = normalizedName === 'home' || normalizedName === 'homepage';

      // Read active status — prefer json_data.page.active (shared with builder-app), fall back to DB field
      const isActive = page.json_data?.page?.active ?? (page.active !== undefined ? page.active : true);

      return {
        name: pageName,
        visible: isActive, // Use active status from API
        selected: isHomePage, // Select Home page by default
        pageId: page.id,
        templatePageId: page.template_page_id,
        jsonData: page.json_data,
        pageType: pageType,
        isHomePage: isHomePage
      } as ToggleCardData & { pageId?: string; templatePageId?: string; jsonData?: any; pageType?: string; isHomePage?: boolean };
    });

    // Find and set current page type to home on initial load
    const homePage = ctx.pages.find((p: any) => p.isHomePage);
    ctx.currentPageType = homePage ? (homePage as any).pageType : 'home';

    // Invalidate pages for navigation cache since pages changed
    ctx._pagesForNavigationCacheKey = '';
    ctx._pagesForNavigation = [];

    this.logger.debug('Loaded pages from website:', ctx.pages);
  }

  /**
   * Load pages from template API response into the pages array for sidebar display
   */
  onPageToggleChange(event: { index: number, item: ToggleCardData }, ctx: DataContext): void {
    // This handles visibility toggle (show/hide page)
    ctx.pages[event.index] = event.item;

    // Force new array reference to trigger change detection
    ctx.pages = [...ctx.pages];

    // Invalidate pages cache since visibility changed
    ctx._pagesForNavigationCacheKey = '';
    ctx._pagesForNavigation = [];

    // Immediately refresh header navigation to reflect changes in preview
    ctx.populateHeaderFromTemplate();

    const page = ctx.pages[event.index] as any;
    const newActiveStatus = event.item.visible;
    const pageId = (event.item as any).pageId;

    if (!pageId) {
      this.logger.warn('No pageId found on toggled page; cannot persist active status.');
      return;
    }

    if (!ctx.websiteId) {
      this.logger.warn('No websiteId in context; cannot persist active status.');
      return;
    }

    // Store active status in json_data.page.active (same pattern as section visibility),
    // so the custom-experience API returns it and the builder-app can filter pages.
    const existingJson = (event.item as any).jsonData || {};
    const updatedJson = { ...existingJson, page: { ...(existingJson.page || {}), active: newActiveStatus } };
    (ctx.pages[event.index] as any).jsonData = updatedJson;

    this.templateService.updateWebsitePage(pageId, updatedJson, '', ctx.websiteId).subscribe({
      next: () => {
        this.logger.debug(`Page active status saved in json_data: ${page.name} → ${newActiveStatus}`);
      },
      error: (err) => {
        this.logger.error('Failed to update page active status:', err);
        // Revert UI on failure
        const revertedItem = { ...event.item, visible: !newActiveStatus };
        ctx.pages[event.index] = revertedItem;
        ctx.pages = [...ctx.pages];
        ctx._pagesForNavigationCacheKey = '';
        ctx.populateHeaderFromTemplate();
      }
    });
  }

  /**
   * Handle page click to switch between pages
   * Called when user clicks on a page card in the sidebar
   */
  /**
   * Handle navigation click from preview components (header buttons, navigation links)
   */
  onNavigationClick(pageType: string, ctx: DataContext): void {
    this.logger.debug('Navigation clicked in website builder:', pageType);
    
    // Find the page with matching pageType
    const targetPage = ctx.pages.find((p: any) => 
      (p as any).pageType === pageType || 
      p.name.toLowerCase().replace(/\s+/g, '-') === pageType.toLowerCase()
    );
    
    if (targetPage) {
      const pageIndex = ctx.pages.indexOf(targetPage);
      this.loadPageData(targetPage as any, pageIndex, ctx);
    } else {
      this.logger.warn('Page not found for pageType:', pageType);
    }
  }

  /**
   * Load page data - common method used by both onPageClick and initialization
   */
  loadPageData(selectedPage: any, pageIndex: number, ctx: DataContext): void {
    // Deselect all pages first
    ctx.pages.forEach((page, idx) => {
      page.selected = idx === pageIndex;
    });

    // Update current page type
    ctx.currentPageType = selectedPage.pageType || 'home';

    // Track website_page_id for updates (use pageId which is the website_page id)
    ctx.websitePageId = selectedPage.pageId || null;
    ctx.templatePageId = selectedPage.templatePageId || null;

    this.logger.debug('🎯 Loading page:', { name: selectedPage.name, pageType: ctx.currentPageType });
    this.logger.debug('📄 Selected page data:', {
      name: selectedPage.name,
      pageType: selectedPage.pageType,
      pageId: selectedPage.pageId,
      hasJsonData: !!selectedPage.jsonData,
      jsonDataKeys: selectedPage.jsonData ? Object.keys(selectedPage.jsonData) : []
    });

    // Always update the JSON data and preview for all pages
    if (selectedPage?.jsonData) {
      // Set template data to the selected page for populate methods
      ctx.templateData = {
        id: selectedPage.templateId || selectedPage.pageId,
        name: selectedPage.name,
        json_data: selectedPage.jsonData
      } as any;

      // Ensure currentJsonData is set properly
      ctx.json = selectedPage.jsonData || {};
      
      // Extract primary color - prioritize website-level branding color over page-level theme color
      if (ctx.website?.json_data?.website?.branding?.colors?.primary) {
        // Website-level branding primary color (highest priority)
        ctx.primaryColor = ctx.website.json_data.website.branding.colors.primary;
      } else if (ctx.json?.page?.layout?.theme?.primary_color) {
        // Page-level theme primary color (fallback)
        ctx.primaryColor = ctx.json.page.layout.theme.primary_color;
      } else {
        // Default fallback
        ctx.primaryColor = '#1B5BFF';
      }
      
      // Extract heading color from website-level branding
      if (ctx.website?.json_data?.website?.branding?.colors?.heading) {
        ctx.headingColor = ctx.website.json_data.website.branding.colors.heading;
      } else {
        // Default fallback for headings
        ctx.headingColor = '#15172E';
      }
      
      // Extract paragraph color from website-level branding
      if (ctx.website?.json_data?.website?.branding?.colors?.paragraph) {
        ctx.paragraphColor = ctx.website.json_data.website.branding.colors.paragraph;
      } else {
        // Default fallback for paragraphs
        ctx.paragraphColor = '#6B7280';
      }
      
      // Extract fonts from website-level branding
      if (ctx.website?.json_data?.website?.branding?.fonts?.heading) {
        ctx.headingFont = ctx.website.json_data.website.branding.fonts.heading;
      } else {
        // Default fallback for heading font
        ctx.headingFont = 'Manrope';
      }
      
      if (ctx.website?.json_data?.website?.branding?.fonts?.body) {
        ctx.bodyFont = ctx.website.json_data.website.branding.fonts.body;
      } else {
        // Default fallback for body font
        ctx.bodyFont = 'Inter';
      }

      try { /* intentionally empty */ } catch (e) {
        this.logger.error('Error stringifying JSON:', e);
      }

      // Update template page ID if available
      if (selectedPage.templatePageId || selectedPage.templateId) {
        ctx.templatePageId = selectedPage.templatePageId || selectedPage.templateId;
      }

      // Update page ID if available (for website pages)
      if (selectedPage.pageId) {
        this.logger.debug('Switching to page:', selectedPage.pageId);
      }

      // Update website data title if available
      if (ctx.json?.page?.title) {
        ctx.websiteData.title = ctx.json.page.title;
      } else if (selectedPage.name) {
        ctx.websiteData.title = selectedPage.name;
      }

      // Ensure base elements and build preview elements from page JSON for all pages
      ctx.ensureBaseElements();
      ctx.buildElementsFromJsonSections();

      // Populate header and footer from website-level + page JSON
      ctx.populateHeaderFromTemplate();
      ctx.populateFooterFromTemplate();

      // Load sections from current page
      this.loadSectionsFromPage(ctx);

      if (selectedPage.isHomePage) {
        this.logger.debug('🔄 Populating Home page sections...');
        this.logger.debug('Available sections in JSON:', Object.keys(ctx.json?.page?.sections || {}));

        ctx.populateHeroFromTemplate();
        ctx.populateSectionsFromTemplate();
        ctx.populateTestimonialsFromTemplate();
        ctx.populateBookingFromTemplate();
        ctx.populateContactFromTemplate();
        ctx.populateHeaderFromTemplate();
        ctx.populateFooterFromTemplate();

        this.logger.debug('✅ Home page populated. Elements:', ctx.elements);
        this.logger.debug('Home page loaded - Editor available');
      } else {
        ctx.showHeroPanel = false;
        this.logger.debug('📄 Non-home page loaded:', { name: selectedPage.name, pageData: ctx.json });
      }

      this.logger.debug('✅ Page data loaded:', {
        currentPageType: ctx.currentPageType,
        hasCurrentJsonData: !!ctx.json,
        currentJsonDataKeys: ctx.json ? Object.keys(ctx.json) : []
      });
    } else {
      this.logger.warn('⚠️ No JSON data found for page:', selectedPage.name);
      ctx.json = {};
    }
  }

  onPageClick(event: { index: number, item: ToggleCardData }, ctx: DataContext): void {
    const selectedPage = ctx.pages[event.index] as any;
    this.loadPageData(selectedPage, event.index, ctx);
  }

  /**
   * Load sections from current page's JSON data
   * Dynamically extracts sections based on page type
   */
  loadSectionsFromPage(ctx: DataContext): void {
    if (!ctx.json) {
      ctx.sections = [];
      return;
    }

    // Section name mapping for display
    const sectionNameMap: Record<string, string> = {
      'header': 'Navigation',
      'info_section': 'About Us',
      'welcome': 'About Us',
      'contact_section': 'Contact',
      'contact': 'Contact',
      'footer': 'Footer',
      'hero_section': 'Hero',
      'hero': 'Hero',
      'testimonials_section': 'Happy Customers',
      'testimonials': 'Happy Customers',
      'booking_section': 'Booking',
      'booking': 'Booking',
      'services_section': 'Services',
      'services': 'Services',
      'package_section': 'Packages',
      'page-title': 'Page Title',
      'coupon_card': 'Coupon',
      'coupon_section': 'Coupon',
      'form_section': 'Form',
      'instructions_section': 'Instructions',
      'instruction_section': 'Instructions'
    };

    // Keys that are NOT sections (page metadata)
    const nonSectionKeys = ['title', 'description', 'path', 'layout', 'theme', 'device', 'grid_system'];

    const sectionsList: (ToggleCardData & { jsonData?: any })[] = [];
    let sectionsSource: Record<string, any> | any[] | null = null;

    // Priority 1: Check for nested structure: page.sections
    if (ctx.json?.page?.sections && typeof ctx.json.page.sections === 'object') {
      sectionsSource = ctx.json.page.sections;
      this.logger.debug('📋 Found sections in page.sections structure');
    }
    // Priority 2: Check for old top-level sections array
    else if (Array.isArray(ctx.json?.sections)) {
      sectionsSource = ctx.json.sections;
      this.logger.debug('📋 Found sections in top-level array structure');
    }
    // Priority 3: Check for sections as direct properties of page object (like Coupons page)
    else if (ctx.json?.page && typeof ctx.json.page === 'object') {
      // Use the page object itself, but filter out non-section keys
      sectionsSource = ctx.json.page;
      this.logger.debug('📋 Found sections as direct properties of page object');
    }

    // Handle object structure (either page.sections or page itself)
    if (sectionsSource && typeof sectionsSource === 'object' && !Array.isArray(sectionsSource)) {
      Object.keys(sectionsSource).forEach((sectionKey) => {
        // Skip non-section keys (metadata)
        if (nonSectionKeys.includes(sectionKey)) {
          return;
        }

        // Only include keys that are explicitly mapped as sections
        if (sectionNameMap[sectionKey]) {
          const sectionData = sectionsSource[sectionKey];
          if (sectionData && typeof sectionData === 'object') {
            const displayName = sectionNameMap[sectionKey];
            // Read active state from section data (default to true if not set)
            const isActive = sectionData.active !== undefined ? sectionData.active : true;
            sectionsList.push({
              name: displayName,
              visible: isActive, // Use active property from API
              selected: false,
              jsonData: sectionData,
              sectionKey: sectionKey // Store the key for updating
            } as ToggleCardData & { jsonData?: any; sectionKey?: string });
          }
        }
      });
    }
    // Handle array structure (old top-level sections)
    else if (Array.isArray(sectionsSource)) {
      sectionsSource.forEach((section: any, index: number) => {
        const sectionId = section.id || section.type || `section_${index}`;
        const displayName = sectionNameMap[sectionId] || this.formatSectionName(sectionId) || 'Section';
        // Read active state from section data (default to true if not set)
        const isActive = section.active !== undefined ? section.active : true;
        sectionsList.push({
          name: displayName,
          visible: isActive, // Use active property from API
          selected: false,
          jsonData: section,
          sectionKey: sectionId // Store the key for updating
        } as ToggleCardData & { jsonData?: any; sectionKey?: string });
      });
    }

    // Sort sections in a logical order (header first, footer last)
    const sectionOrder = ['Navigation', 'Hero', 'About Us', 'Express Details', 'Services', 'Packages', 'Form', 'Coupon', 'Instructions', 'Happy Customers', 'Booking', 'Contact', 'Footer'];
    sectionsList.sort((a, b) => {
      const indexA = sectionOrder.indexOf(a.name);
      const indexB = sectionOrder.indexOf(b.name);
      if (indexA !== -1 && indexB !== -1) return indexA - indexB;
      if (indexA !== -1) return -1;
      if (indexB !== -1) return 1;
      return a.name.localeCompare(b.name);
    });

    ctx.sections = sectionsList;
    // Invalidate visibility map cache since sections changed
    ctx._sectionVisibilityMapCacheKey = '';
    ctx._sectionVisibilityMap = {};
    this.logger.debug('📋 Sections loaded:', ctx.sections.map(s => s.name));
  }

  /**
   * Format section key to display name
   */
  private formatSectionName(key: string): string {
    return key
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Map section names to element IDs for visibility control
   * Works for all page types (Home, Express Wash Packages, Express Auto Details, Fleet, Coupons)
   */
  getSectionVisibilityMap(ctx: DataContext): Record<string, boolean> {
    // Create cache key from sections array
    const cacheKey = ctx.sections.map(s => `${s.name}:${s.visible}`).join('|');

    // Return cached result if sections haven't changed
    if (cacheKey === ctx._sectionVisibilityMapCacheKey && Object.keys(ctx._sectionVisibilityMap).length > 0) {
      return ctx._sectionVisibilityMap;
    }

    // Recompute visibility map
    const visibilityMap: Record<string, boolean> = {};

    // Map section display names to section keys used in preview components
    const sectionToKeyMap: Record<string, string[]> = {
      // Common sections
      'Navigation': ['header'],
      'Hero': ['hero', 'hero_section'],
      'Footer': ['footer'],

      // Home page sections
      'About Us': ['welcome'],
      'Express Details': ['services'],
      'Services': ['services', 'services_section'], // Works for both Home and Express Auto Details
      'Happy Customers': ['testimonials'],
      'Booking': ['booking'],
      'Contact': ['contact'],

      // Express Wash Packages sections
      'Packages': ['package_section'],
      'Page Title': ['page-title', 'hero_section'],

      // Fleet Management sections
      'Form': ['form_section'],
      'Form Section': ['form_section'],

      // Coupons sections
      'Coupon': ['coupon_card', 'coupon_section'],
      'Instructions': ['instructions_section', 'instruction_section']
    };

    // Build visibility map from current sections
    ctx.sections.forEach((section) => {
      const sectionKeys = sectionToKeyMap[section.name] || [];
      // Also check if section name directly matches a key (for dynamic section names)
      if (!sectionKeys.length) {
        // Try to derive key from section name (convert "Packages" to "package_section", etc.)
        const derivedKey = this.deriveSectionKey(section.name);
        if (derivedKey) {
          sectionKeys.push(derivedKey);
        }
      }

      // Use visible property which is synced with active property from API
      // Priority: use section.visible (synced with toggle state), fallback to jsonData.active
      const sectionData = (section as any).jsonData;
      const isVisible = section.visible !== undefined
        ? section.visible
        : (sectionData?.active !== undefined ? sectionData.active : true);

      sectionKeys.forEach((key) => {
        visibilityMap[key] = isVisible;
      });

      // Also add the section name itself as a key (for flexibility)
      visibilityMap[section.name.toLowerCase().replace(/\s+/g, '_')] = isVisible;
    });

    // Cache the result
    ctx._sectionVisibilityMap = visibilityMap;
    ctx._sectionVisibilityMapCacheKey = cacheKey;

    return visibilityMap;
  }

  /**
   * Derive section key from display name
   */
  private deriveSectionKey(displayName: string): string | null {
    // Convert display names like "Package Section" to "package_section"
    const key = displayName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');

    return key || null;
  }

  /**
  * Handles a section visibility toggle change, updates the section state in the data context, invalidates cached visibility data, synchronizes the active flag in the underlying JSON, and persists the change.
  * @example
  * nSectionToggleChange({ index: 0, item: toggleCardData }, ctx)
  * void
  * @param {{ index: number, item: ToggleCardData }} event - Toggle event containing the section index and updated toggle card item.
  * @param {DataContext} ctx - Current builder data context containing sections, JSON data, and state management helpers.
  * @returns {void} Nothing.
  **/
  onSectionToggleChange(event: { index: number, item: ToggleCardData }, ctx: DataContext): void {
    ctx.sections[event.index] = event.item;
    
    // Record state for undo/redo (immediate - toggle is a discrete action)
    ctx.recordStateChange('Section visibility toggled', true);
    
    // Invalidate visibility map cache since section visibility changed
    ctx._sectionVisibilityMapCacheKey = '';
    ctx._sectionVisibilityMap = {};

    // Update JSON data active state in currentJsonData
    const section = ctx.sections[event.index] as any;
    if (section?.jsonData && section?.sectionKey && ctx.json) {
      const newActiveState = section.visible;
      this.logger.debug(`Section "${section.name}" active state changed to:`, newActiveState);

      // Priority 1: Update in currentJsonData.page.sections[sectionKey].active (nested structure)
      if (ctx.json?.page?.sections?.[section.sectionKey]) {
        ctx.json.page.sections[section.sectionKey].active = newActiveState;
      }
      // Priority 2: Update in currentJsonData.page[sectionKey].active (direct properties, like Coupons page)
      else if (ctx.json?.page?.[section.sectionKey]) {
        ctx.json.page[section.sectionKey].active = newActiveState;
      }

      // Also update in jsonData stored in section
      section.jsonData.active = newActiveState;

      // Save the change to API immediately
      this.saveSectionVisibility(ctx);
    }
  }

  /**
   * Save section visibility changes to API
   */
  saveSectionVisibility(ctx: DataContext): void {
    if (!ctx.websiteId || !ctx.websitePageId) {
      this.logger.warn('Missing websiteId or websitePageId; cannot save section visibility.');
      return;
    }

    if (!ctx.json) {
      this.logger.warn('No current JSON data; cannot save section visibility.');
      return;
    }

    // Clean up old structure before saving
    const cleanedJsonData = ctx.cleanupJsonStructure(ctx.json);

    // Generate HTML data from updated JSON
    const htmlData = ctx.generateHtmlFromJson(cleanedJsonData);

    // Update the website page via API
    this.templateService.updateWebsitePage(
      ctx.websitePageId,
      cleanedJsonData,
      htmlData,
      ctx.websiteId
    ).subscribe({
      next: () => {
        this.logger.debug('✅ Section visibility updated successfully');
        // Update editableJson to reflect changes
        try {
          // Also update currentJsonData to the cleaned version
          ctx.json = cleanedJsonData;
        } catch (e) {
          this.logger.error('Error stringifying JSON:', e);
        }
      },
      error: (err) => {
        this.logger.error('❌ Failed to update section visibility:', err);
      }
    });
  }

  // Action methods - cleaned up for reusability

  /**
   * Loads page metadata from a list of templates into the provided data context and sets the current page to Home when available.
   * @example
   * loadPagesFromTemplate(templates, ctx)
   * ctx.pages
   * @param {any[]} templates - Array of template objects returned from the API.
   * @param {DataContext} ctx - Data context to populate with pages and current page state.
   * @returns {void} Does not return a value.
   **/
  loadPagesFromTemplate(templates: any[], ctx: DataContext): void {
    if (!Array.isArray(templates) || templates.length === 0) {
      this.logger.warn('No templates found in API response');
      ctx.pages = [];
      return;
    }

    ctx.pages = templates.map((template: any, index: number) => {
      // Use the actual page name from API
      let pageName = 'Page ' + (index + 1);

      if (template?.json_data?.page?.title) {
        pageName = template.json_data.page.title;
      } else if (template?.name) {
        pageName = template.name;
      }

      // Set page type based on name (lowercase, no spaces)
      // Normalize page names to match expected page types
      const normalizedName = pageName.toLowerCase().trim();
      let pageType = normalizedName.replace(/\s+/g, '-').replace(/\|/g, '').replace(/[^a-z0-9-]/g, '');

      // Map common page name variations to expected page types
      const pageTypeMap: Record<string, string> = {
        'express-washes': 'express-wash-packages',
        'express-wash': 'express-wash-packages',
        'fleets': 'fleet-management',
        'fleet': 'fleet-management',
        'coupons': 'car-wash-coupons--way',
        'coupon': 'car-wash-coupons--way',
        'express-details': 'express-auto-details',
        'express-auto-details': 'express-auto-details',
        'home': 'home',
        'homepage': 'home'
      };

      // Use mapped page type if available, otherwise use generated one
      if (pageTypeMap[pageType]) {
        pageType = pageTypeMap[pageType];
      } else if (pageTypeMap[normalizedName]) {
        pageType = pageTypeMap[normalizedName];
      }

      // Check if this is the Home page (will be editable)
      const isHomePage = normalizedName === 'home' || normalizedName === 'homepage';

      return {
        name: pageName,
        visible: true,
        selected: isHomePage, // Select Home page by default
        templateId: template.id,
        jsonData: template.json_data,
        pageType: pageType,
        isHomePage: isHomePage
      } as ToggleCardData & { templateId?: string; jsonData?: any; pageType?: string; isHomePage?: boolean };
    });

    // Find and set current page type to home on initial load
    const homePage = ctx.pages.find((p: any) => p.isHomePage);
    ctx.currentPageType = homePage ? (homePage as any).pageType : 'home';

    // Invalidate pages for navigation cache since pages changed
    ctx._pagesForNavigationCacheKey = '';
    ctx._pagesForNavigation = [];

    this.logger.debug('Loaded pages from template:', ctx.pages);
  }
}